import { useCallback } from 'react';
import { SlashCommand } from '../slash-commands/slash-commands-config';

// Utility function to satisfy the linter's no-empty-function rule
const noop = () => undefined;

interface UseSlashCommandActionsProps {
  onImageClick: () => void;
  onEmojiSelect: () => void;
  onVariableClick: () => void;
  onShortLinkClick: () => void;
  onContactClick: () => void;
  handleCommand: (command: SlashCommand) => void;
}

/**
 * Hook to manage slash command actions in a clean, decoupled way.
 * Separates the command handling logic from the UI implementation.
 *
 * Key features:
 * - Centralizes all slash command actions
 * - Provides type-safe action handlers
 * - Decouples command logic from UI implementation
 * - Makes testing and maintenance easier
 *
 * @param props - Object containing callbacks for different actions
 * @returns Object containing handlers for each command type
 */
export function useSlashCommandActions({
  onImageClick,
  onEmojiSelect,
  onVariableClick,
  onShortLinkClick,
  onContactClick,
  handleCommand
}: UseSlashCommandActionsProps) {
  /**
   * Handles the emoji command by:
   * 1. Cleaning up the slash command UI
   * 2. Triggering the emoji picker
   */
  const handleEmojiCommand = useCallback(() => {
    // First clean up the slash command
    handleCommand({
      label: 'Emoji',
      icon: null,
      description: '',
      action: noop,
      category: 'Media'
    });
    // Then trigger the emoji picker
    onEmojiSelect();
  }, [handleCommand, onEmojiSelect]);

  /**
   * Handles the image command by:
   * 1. Cleaning up the slash command UI
   * 2. Opening the image dialog
   */
  const handleImageCommand = useCallback(() => {
    handleCommand({
      label: 'Image',
      icon: null,
      description: '',
      action: noop,
      category: 'Media'
    });
    onImageClick();
  }, [handleCommand, onImageClick]);

  /**
   * Handles the variable command by:
   * 1. Cleaning up the slash command UI
   * 2. Opening the variable dialog
   */
  const handleVariableCommand = useCallback(() => {
    handleCommand({
      label: 'Variable',
      icon: null,
      description: '',
      action: noop,
      category: 'Embeds'
    });
    onVariableClick();
  }, [handleCommand, onVariableClick]);

  /**
   * Handles the shortlink command by:
   * 1. Cleaning up the slash command UI
   * 2. Opening the shortlink dialog
   */
  const handleShortLinkCommand = useCallback(() => {
    handleCommand({
      label: 'Link',
      icon: null,
      description: '',
      action: noop,
      category: 'Embeds'
    });
    onShortLinkClick();
  }, [handleCommand, onShortLinkClick]);

  /**
   * Handles the contact command by:
   * 1. Cleaning up the slash command UI
   * 2. Opening the contact dialog
   */
  const handleContactCommand = useCallback(() => {
    handleCommand({
      label: 'Contact',
      icon: null,
      description: '',
      action: noop,
      category: 'Embeds'
    });
    onContactClick();
  }, [handleCommand, onContactClick]);

  return {
    handleEmojiCommand,
    handleImageCommand,
    handleVariableCommand,
    handleShortLinkCommand,
    handleContactCommand
  };
}
